import React, { useState, useEffect, useCallback, useRef } from 'react';

const EXPRESSIONS = [
  'smug',
  'cooked',
  'euphoric',
  'shocked',
  'dead_inside',
  'scheming',
  'crying_laughing',
];

const MOUTH_PATHS = {
  smug: 'M 38,72 Q 50,82 62,72',
  cooked: 'M 38,78 Q 50,68 62,78',
  euphoric: 'M 35,70 Q 50,90 65,70 Z',
  shocked: 'M 42,72 Q 50,85 58,72 Q 50,78 42,72 Z',
  dead_inside: 'M 38,75 L 62,75',
  scheming: 'M 38,72 Q 50,80 55,70',
  crying_laughing: 'M 35,70 Q 50,88 65,70 Z',
};

const EXTRA_DETAILS = {
  smug: null,
  cooked: (
    <>
      <line x1="30" y1="50" x2="20" y2="42" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
      <line x1="70" y1="50" x2="80" y2="42" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
      <text x="75" y="35" fontSize="12" fontFamily="Caveat" fill="#1a1a1a" transform="rotate(15, 75, 35)">help</text>
    </>
  ),
  euphoric: (
    <>
      <text x="15" y="30" fontSize="14" fontFamily="Caveat" fill="#1a1a1a" transform="rotate(-20, 15, 30)">✨</text>
      <text x="78" y="28" fontSize="14" fontFamily="Caveat" fill="#1a1a1a" transform="rotate(15, 78, 28)">✨</text>
      <line x1="25" y1="55" x2="18" y2="48" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="75" y1="55" x2="82" y2="48" stroke="#1a1a1a" strokeWidth="1.5" strokeLinecap="round" />
    </>
  ),
  shocked: (
    <>
      <line x1="28" y1="45" x2="22" y2="38" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
      <line x1="72" y1="45" x2="78" y2="38" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
      <text x="72" y="30" fontSize="16" fontFamily="Permanent Marker" fill="#1a1a1a">!</text>
    </>
  ),
  dead_inside: (
    <>
      <line x1="36" y1="54" x2="42" y2="60" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="42" y1="54" x2="36" y2="60" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="58" y1="54" x2="64" y2="60" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="64" y1="54" x2="58" y2="60" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
    </>
  ),
  scheming: (
    <>
      <line x1="30" y1="52" x2="22" y2="52" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
      <line x1="70" y1="48" x2="78" y2="46" stroke="#1a1a1a" strokeWidth="2" strokeLinecap="round" />
    </>
  ),
  crying_laughing: (
    <>
      <path d="M 34,62 Q 32,70 34,68" stroke="#4a9eff" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M 66,62 Q 68,70 66,68" stroke="#4a9eff" strokeWidth="2" fill="none" strokeLinecap="round" />
    </>
  ),
};

export default function DoodMascot({ size = 280 }) {
  const [expression, setExpression] = useState('smug');
  const [eyeOffset, setEyeOffset] = useState({ x: 0, y: 0 });
  const [isClicked, setIsClicked] = useState(false);
  const svgRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!svgRef.current) return;
    const rect = svgRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const dx = e.clientX - centerX;
    const dy = e.clientY - centerY;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const maxOffset = 4;
    const factor = Math.min(dist / 300, 1);
    setEyeOffset({
      x: (dx / (dist || 1)) * maxOffset * factor,
      y: (dy / (dist || 1)) * maxOffset * factor,
    });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  const handleClick = () => {
    setIsClicked(true);
    const currentIdx = EXPRESSIONS.indexOf(expression);
    let nextIdx;
    do {
      nextIdx = Math.floor(Math.random() * EXPRESSIONS.length);
    } while (nextIdx === currentIdx);
    setExpression(EXPRESSIONS[nextIdx]);
    setTimeout(() => setIsClicked(false), 200);
  };

  const showEyes = expression !== 'dead_inside';

  return (
    <svg
      ref={svgRef}
      width={size}
      height={size}
      viewBox="0 0 100 100"
      onClick={handleClick}
      style={{
        cursor: 'pointer',
        transform: isClicked ? 'scale(0.92) rotate(-5deg)' : 'scale(1) rotate(0deg)',
        transition: 'transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
        filter: 'drop-shadow(3px 3px 0px rgba(0,0,0,0.1))',
      }}
      className="dood-mascot"
    >
      {/* Body - blobby worm/goblin shape */}
      <path
        d="M 25,85
           Q 10,80 12,60
           Q 8,45 18,35
           Q 22,20 35,18
           Q 45,10 55,12
           Q 68,10 78,22
           Q 90,32 88,50
           Q 92,65 82,78
           Q 78,88 65,90
           Q 50,95 35,90
           Z"
        fill="#f5f3ee"
        stroke="#1a1a1a"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Wonky inner body line for texture */}
      <path
        d="M 30,82 Q 20,70 22,55 Q 18,42 28,35"
        fill="none"
        stroke="#1a1a1a"
        strokeWidth="0.8"
        strokeLinecap="round"
        opacity="0.3"
      />

      {/* Left eye */}
      {showEyes && (
        <g>
          <ellipse
            cx={39 + eyeOffset.x * 0.3}
            cy={57 + eyeOffset.y * 0.3}
            rx="8"
            ry="9"
            fill="white"
            stroke="#1a1a1a"
            strokeWidth="2"
          />
          <circle
            cx={39 + eyeOffset.x}
            cy={57 + eyeOffset.y}
            r="4"
            fill="#1a1a1a"
          />
          <circle
            cx={37 + eyeOffset.x}
            cy={55 + eyeOffset.y}
            r="1.5"
            fill="white"
          />
        </g>
      )}

      {/* Right eye */}
      {showEyes && (
        <g>
          <ellipse
            cx={61 + eyeOffset.x * 0.3}
            cy={57 + eyeOffset.y * 0.3}
            rx="8"
            ry="9"
            fill="white"
            stroke="#1a1a1a"
            strokeWidth="2"
          />
          <circle
            cx={61 + eyeOffset.x}
            cy={57 + eyeOffset.y}
            r="4"
            fill="#1a1a1a"
          />
          <circle
            cx={59 + eyeOffset.x}
            cy={55 + eyeOffset.y}
            r="1.5"
            fill="white"
          />
        </g>
      )}

      {/* Eyebrows based on expression */}
      {expression === 'smug' && (
        <>
          <line x1="32" y1="44" x2="44" y2="46" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="56" y1="44" x2="68" y2="42" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
        </>
      )}
      {expression === 'euphoric' && (
        <>
          <path d="M 30,44 Q 37,38 44,44" stroke="#1a1a1a" strokeWidth="2" fill="none" strokeLinecap="round" />
          <path d="M 56,44 Q 63,38 70,44" stroke="#1a1a1a" strokeWidth="2" fill="none" strokeLinecap="round" />
        </>
      )}
      {expression === 'shocked' && (
        <>
          <path d="M 30,42 Q 37,36 44,42" stroke="#1a1a1a" strokeWidth="2.5" fill="none" strokeLinecap="round" />
          <path d="M 56,42 Q 63,36 70,42" stroke="#1a1a1a" strokeWidth="2.5" fill="none" strokeLinecap="round" />
        </>
      )}
      {expression === 'scheming' && (
        <>
          <line x1="30" y1="48" x2="44" y2="44" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="56" y1="44" x2="70" y2="48" stroke="#1a1a1a" strokeWidth="2.5" strokeLinecap="round" />
        </>
      )}

      {/* Mouth */}
      <path
        d={MOUTH_PATHS[expression]}
        fill={['euphoric', 'shocked', 'crying_laughing'].includes(expression) ? '#1a1a1a' : 'none'}
        stroke="#1a1a1a"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Expression-specific extras */}
      {EXTRA_DETAILS[expression]}

      {/* Little stubby arms */}
      <path
        d="M 18,60 Q 8,58 10,65"
        fill="none"
        stroke="#1a1a1a"
        strokeWidth="2.5"
        strokeLinecap="round"
      />
      <path
        d="M 82,58 Q 92,56 90,63"
        fill="none"
        stroke="#1a1a1a"
        strokeWidth="2.5"
        strokeLinecap="round"
      />

      {/* Little feet */}
      <ellipse cx="38" cy="92" rx="8" ry="3" fill="#f5f3ee" stroke="#1a1a1a" strokeWidth="2" />
      <ellipse cx="62" cy="92" rx="8" ry="3" fill="#f5f3ee" stroke="#1a1a1a" strokeWidth="2" />
    </svg>
  );
}
